/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import dragula from 'dragula';
import { task, timeout } from 'ember-concurrency';
import { type FunctionBasedModifier, modifier } from 'ember-modifier';
import { indexBy } from 'volta/utils/array-utils';

import { action } from '@ember/object';
import { isPresent } from '@ember/utils';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { DragulaOptions, Drake } from 'dragula';
import type Owner from '@ember/owner';
import type { WithBoundArgs } from '@glint/template';
import type BvDndContainerComponent from './container';
import type { AutoAnimateModifierOptions } from 'volta/modifiers/autoanimate';

export type DndModifierType = FunctionBasedModifier<{
  Args: {
    Positional: [string];
    Named: {};
  };
  Element: HTMLElement;
}>;

export type HandleModifierType = FunctionBasedModifier<{
  Args: {
    Positional: [string];
    Named: {
      disabled?: boolean;
    };
  };
  Element: HTMLElement;
}>;

interface BvDndSignature {
  Args: {
    accepts?: (el?: Element, target?: Element) => boolean;
    animateOptions?: AutoAnimateModifierOptions;
    direction?: 'vertical' | 'horizontal';
    disabled?: boolean;
    dragIdProperty?: string;
    invalid?: (el?: Element, target?: Element) => boolean;
    noHandle?: boolean;
    onCancel?: (el: Element, container: Element, source: Element) => void;
    onCloned?: (clone: Element, original: Element, type: 'mirror' | 'copy') => void;
    onDrag?: (el: Element, source: Element) => void;
    onDragEnd?: (el: Element) => void;
    onDrop?: (items: any[][]) => void;
    onOut?: (el: Element, container: Element, source: Element) => void;
    onOver?: (el: Element, container: Element, source: Element) => void;
    onReady?: (drake: Drake) => void;
    onRemove?: (item: any) => void;
    onShadow?: (el: Element, container: Element, source: Element) => void;
    options?: DragulaOptions;
    removeOnSpill?: boolean;
  };
  Blocks: {
    default: [
      {
        Container: WithBoundArgs<
          typeof BvDndContainerComponent,
          'onInsert' | 'onUpdate' | 'onDestroy' | 'animate' | 'animateOptions'
        >;
        dndModifier: DndModifierType;
        dndHandle: HandleModifierType;
        onInsert: (element: HTMLElement, [items]: [object[]]) => void;
        onDestroy: (element: HTMLElement) => void;
        onUpdate: (element: HTMLElement, [items]: [object[]]) => void;
      }
    ];
  };
}

type TDragEvent =
  | 'drag'
  | 'dragend'
  | 'drop'
  | 'cancel'
  | 'remove'
  | 'shadow'
  | 'over'
  | 'out'
  | 'cloned';

const events: Record<TDragEvent, keyof BvDndSignature['Args']> = {
  drag: 'onDrag',
  dragend: 'onDragEnd',
  drop: 'onDrop',
  cancel: 'onCancel',
  remove: 'onRemove',
  shadow: 'onShadow',
  over: 'onOver',
  out: 'onOut',
  cloned: 'onCloned'
};

export const DRAG_ATTRIBUTE = 'data-drag-id';
export const DEFAULT_HANDLE_CLASSNAME = 'bv-dnd-handle';
export const DRAG_ITEM_CLASSNAME = 'bv-dnd-drag-item';

type DragData = { container: HTMLElement; items: unknown[] }[];
type CustomDrake = Drake & { data: DragData };

// Thanks to https://github.com/zestia/ember-dragula
export default class BvDnd extends Component<BvDndSignature> {
  static events = events;

  @tracked drake!: CustomDrake;

  get dataIdAttr() {
    return this.args.dragIdProperty ?? 'id';
  }

  constructor(owner: Owner, args: BvDndSignature['Args']) {
    super(owner, args);
    const options: DragulaOptions = {
      ...this.args.options,
      removeOnSpill: !!this.args.onRemove ?? false
    };

    if (!args.noHandle) {
      options.moves = function (_el?: Element, _container?: Element, handle?: Element) {
        return handle
          ? ((handle.classList.contains(DEFAULT_HANDLE_CLASSNAME) ||
              handle.parentElement?.classList.contains(DEFAULT_HANDLE_CLASSNAME)) ??
              false)
          : true;
      };
    }

    if (args.disabled) {
      options.invalid = function (_el?: Element, _handle?: Element) {
        return args.disabled ?? false;
      };
    } else if (args.invalid) {
      options.invalid = args.invalid;
    }

    if (args.accepts) {
      options.accepts = args.accepts;
    }

    this.drake = dragula(options) as CustomDrake;
    this._setupHandlers();
    this.args.onReady?.(this.drake);
  }

  dragItem: DndModifierType = modifier((element: HTMLElement, [id]: [string]) => {
    element.setAttribute(DRAG_ATTRIBUTE, id);
    element.classList.add(DRAG_ITEM_CLASSNAME);
    return () => {
      element.removeAttribute(DRAG_ATTRIBUTE);
      element.classList.remove(DRAG_ITEM_CLASSNAME);
    };
  });

  handle: HandleModifierType = modifier(
    (element: HTMLElement, [_id]: [string], { disabled }: { disabled?: boolean }) => {
      element.classList.add(DEFAULT_HANDLE_CLASSNAME);

      if (disabled === true) {
        element.setAttribute('disabled', 'true');
        element.removeEventListener('touchmove', this.preventDefault);
      } else {
        element.removeAttribute('disabled');
        element.addEventListener('touchmove', this.preventDefault);
      }
      return () => {
        element.classList.remove(DEFAULT_HANDLE_CLASSNAME);
        element.removeAttribute('disabled');
        element.removeEventListener('touchmove', this.preventDefault);
      };
    }
  );

  @action
  addContainer(element: HTMLElement, [items]: [object[]]) {
    this.drake.containers.push(element);
    const dataItems = this.deserialize(items);

    this.drake.data = [...(this.drake.data ?? []), { container: element, items: dataItems }];
  }

  @action
  removeContainer(element: HTMLElement) {
    this.drake.containers.splice(this.drake.containers.indexOf(element), 1);
    this.drake.data = this.drake.data.filter((data) => data.container !== element);
  }

  @action
  updateContainer(element: HTMLElement, [items]: [object[]]) {
    const { data } = this.drake;
    const containerIndex = data.findIndex((d) => d.container === element);
    if (data[containerIndex]?.container === element && isPresent(containerIndex)) {
      this.drake.data[containerIndex]!.items = items;
    }
  }

  @action
  onDrop(el: Element, target: Element, source: Element, _sibling: Element) {
    this.args.onDrop?.(this.getUpdatedItems());
    if (target !== source) {
      el.remove();
    }
  }

  @action
  onRemove(el: Element, _container: Element, _source: Element) {
    const { data } = this.drake;
    const flatItems = data.map((d) => d.items).flat(2);
    const allItems = indexBy<unknown>(flatItems, this.dataIdAttr as keyof unknown);
    const id = el.getAttribute(DRAG_ATTRIBUTE);
    if (id && allItems[id]) {
      this.args.onRemove?.(allItems[id]!);
    }
  }

  @action
  onCancel(el: Element, _container: Element, _source: Element) {
    this.onCancelTask.perform(el);
  }

  onCancelTask = task(async (el: Element) => {
    el.classList.add('dnd-on-cancel');
    await timeout(1000);
    el.classList.remove('dnd-on-cancel');
  });

  getUpdatedItems(): unknown[][] {
    const { data } = this.drake;
    const flatItems = this.deserialize(data.map((d) => d.items).flat(2));
    const allItems = indexBy<unknown>(flatItems, this.dataIdAttr as keyof unknown);
    const sortedItems: unknown[] = [];

    for (const containerData of data) {
      const { container } = containerData;
      const containerItems = [];
      const children = container.querySelectorAll(`[${DRAG_ATTRIBUTE}]`);

      for (const child of children) {
        const id = child.getAttribute(DRAG_ATTRIBUTE);
        if (id) {
          containerItems.push(allItems[id]);
        }
      }

      sortedItems.push(containerItems);
    }

    return sortedItems as unknown[][];
  }

  deserialize(items: unknown[]) {
    let _items = items;
    if (_items.length > 0 && Array.isArray(_items[0])) {
      _items = _items[0].slice();
    }
    return _items;
  }

  willDestroy() {
    super.willDestroy();
    this.drake?.destroy();
  }

  preventDefault(e: TouchEvent) {
    e.preventDefault();
  }

  private _setupHandlers() {
    this.drake.on('drop', this.onDrop);
    this.drake.on('cancel', this.onCancel);
    this.drake.on('remove', this.onRemove);

    if (typeof this.args.onDrag === 'function') {
      this.drake.on('drag', this.args.onDrag);
    }

    if (typeof this.args.onDragEnd === 'function') {
      this.drake.on('dragend', this.args.onDragEnd);
    }

    if (typeof this.args.onShadow === 'function') {
      this.drake.on('shadow', this.args.onShadow);
    }

    if (typeof this.args.onOver === 'function') {
      this.drake.on('over', this.args.onOver);
    }

    if (typeof this.args.onOut === 'function') {
      this.drake.on('out', this.args.onOut);
    }

    if (typeof this.args.onCloned === 'function') {
      this.drake.on('cloned', this.args.onCloned);
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    BvDnd: typeof BvDnd;
    'bv-dnd': typeof BvDnd;
  }
}
